/**
 * Arabic Coin Pluralization Utility
 * 
 * Proper Arabic grammar for counted nouns (عملة):
 * - 1: singular (عملة)
 * - 2: dual (عملتان)
 * - 3-10: plural (عملات)
 * - 11-99: singular (عملة)
 * - 100+: singular pattern (عملة)
 * 
 * Usage:
 *   import { formatCoinsAr, formatCoins } from '../lib/coinFormat';
 *   formatCoinsAr(5)  // "5 عملات"
 *   formatCoins(5, 'ar')  // "5 عملات"
 *   formatCoins(5, 'en')  // "5 coins"
 */

/**
 * Get the correct Arabic word for coins based on number
 * @param {number} n - Number of coins
 * @returns {string} Arabic word for coins
 */
export function arabicCoinWord(n) {
  const num = Math.abs(Number(n) || 0);
  const mod100 = num % 100;

  if (num === 0) return "عملة";
  if (num === 1) return "عملة";
  if (num === 2) return "عملتان";
  if (mod100 >= 3 && mod100 <= 10) return "عملات";
  if (mod100 >= 11 && mod100 <= 99) return "عملة";
  return "عملة";
}

/**
 * Get the correct English word for coins based on number
 * @param {number} n - Number of coins
 * @returns {string} English word for coins
 */
export function englishCoinWord(n) {
  const num = Math.abs(Number(n) || 0);
  return num === 1 ? "coin" : "coins";
}

/**
 * Convert Western Arabic digits (0-9) to Eastern Arabic (٠-٩)
 * @param {string|number} input - Number or string to convert
 * @returns {string} String with Eastern Arabic digits
 */
export function toEasternArabicDigits(input) {
  const map = {
    "0": "٠", "1": "١", "2": "٢", "3": "٣", "4": "٤",
    "5": "٥", "6": "٦", "7": "٧", "8": "٨", "9": "٩"
  };
  return String(input).replace(/[0-9]/g, (d) => map[d]);
}

/**
 * Format coins with proper Arabic grammar
 * @param {number} n - Number of coins
 * @param {Object} opts - Options
 * @param {boolean} opts.showNumber - Whether to show the number (default: true)
 * @param {boolean} opts.useEasternDigits - Use Eastern Arabic digits ١٢٣ (default: false)
 * @returns {string} Formatted string
 */
export function formatCoinsAr(n, opts = {}) {
  const {
    showNumber = true,
    useEasternDigits = false,
  } = opts;

  const num = Number(n) || 0;
  const word = arabicCoinWord(num);

  const formattedNumber = useEasternDigits
    ? toEasternArabicDigits(String(num))
    : String(num);

  return showNumber ? `${formattedNumber} ${word}` : word;
}

/**
 * Format coins with proper English grammar
 * @param {number} n - Number of coins
 * @param {Object} opts - Options
 * @param {boolean} opts.showNumber - Whether to show the number (default: true)
 * @returns {string} Formatted string
 */
export function formatCoinsEn(n, opts = {}) {
  const { showNumber = true } = opts;
  const num = Number(n) || 0;
  const word = englishCoinWord(num);
  return showNumber ? `${num} ${word}` : word;
}

/**
 * Format coins based on language
 * @param {number} n - Number of coins
 * @param {string} lang - Language code ('ar' or 'en')
 * @param {Object} opts - Options
 * @returns {string} Formatted string
 */
export function formatCoins(n, lang = 'en', opts = {}) {
  const isArabic = lang?.toLowerCase?.().startsWith('ar');
  return isArabic ? formatCoinsAr(n, opts) : formatCoinsEn(n, opts);
}

/**
 * Format price/cost with coin suffix
 * Good for buttons like "Purchase (5 coins)"
 * @param {number} n - Price in coins
 * @param {string} lang - Language code
 * @returns {string} Formatted price string
 */
export function formatCoinPrice(n, lang = 'en') {
  const isArabic = lang?.toLowerCase?.().startsWith('ar');
  const num = Number(n) || 0;
  
  if (isArabic) {
    return formatCoinsAr(num);
  }
  
  return `${num} ${num === 1 ? 'coin' : 'coins'}`;
}

/**
 * Format balance display (larger numbers with separators)
 * @param {number} n - Balance
 * @param {string} lang - Language code
 * @returns {string} Formatted balance
 */
export function formatCoinBalance(n, lang = 'en') {
  const isArabic = lang?.toLowerCase?.().startsWith('ar');
  const num = Number(n) || 0;
  
  // Add thousand separators
  const formatted = num.toLocaleString(isArabic ? 'ar-SA' : 'en-US');
  
  if (isArabic) {
    return `${formatted} ${arabicCoinWord(num)}`;
  }
  
  return `${formatted} ${englishCoinWord(num)}`;
}

export default {
  formatCoins,
  formatCoinsAr,
  formatCoinsEn,
  formatCoinPrice,
  formatCoinBalance,
  arabicCoinWord,
  englishCoinWord,
  toEasternArabicDigits,
};
